import {Injectable, inject} from '@angular/core'
import {type Observable, catchError, map, of, shareReplay, switchMap, tap, throwError} from 'rxjs'
import {GalleryV3Service} from '@services/gallery-v3.service'
import type {GalleryDataAdapter} from '@uve-solutions/f-gallery/app'
import type {
  AutocompleteCategory,
  AutocompleteResult,
  GalleryEntity,
  GalleryFolderTree,
  GalleryItem,
  GalleryItemDetail,
  GalleryPaginatedResponse,
  GallerySearchParams,
  GalleryVisibility,
} from '@uve-solutions/f-gallery/models'

const SEARCH_COLUMNS = [
  'id',
  'name',
  'description',
  'creationDate',
  'tags',
  'tagsCloud',
  'mimeType',
  'readOnly',
  'urlThumbnail',
  'url',
  'type',
  'visibility',
  'path',
  'pathCloud',
  'entities',
  'coordX',
  'coordY',
]

interface RawGalleryEntity {
  id?: string | number
  code?: string
  name?: string
  businessName?: string
  type?: string
  hierarchyLevel?: string | null
}

interface RawGalleryTag {
  id?: string | number
  name?: string
}

interface RawGalleryItem {
  id: number
  name?: string
  description?: string
  url?: string
  urlThumbnail?: string | null
  type?: string
  mimeType?: string
  creationDate?: string
  readOnly?: boolean
  visibility?: string | {id?: string; name?: string; roles?: string[]} | null
  path?: string
  pathCloud?: string
  entities?: RawGalleryEntity[] | null
  tags?: RawGalleryTag[]
  tagsCloud?: RawGalleryTag[]
  coordX?: number | null
  coordY?: number | null
  manager?: {userCode?: string; id?: string | number; name?: string; email?: string} | null
}

interface RawTreeNode {
  id?: number
  title?: string
  name?: string
  readonly?: boolean
  readOnly?: boolean
  nodes?: RawTreeNode[]
  children?: RawTreeNode[]
}

interface RawSearchResponse {
  data?: RawGalleryItem[]
  pagination?: {total?: number}
}

@Injectable({providedIn: 'root'})
export class CartGalleryDataAdapter implements GalleryDataAdapter {
  readonly #gallery = inject(GalleryV3Service)

  #rootFolderId: number | null = null
  #tree$: Observable<GalleryFolderTree> | null = null

  searchItems(params: GallerySearchParams): Observable<GalleryPaginatedResponse> {
    const isRootLevel = params.folderId !== -1 && (params.folderId == null || params.folderId === 0)
    if (isRootLevel && this.#rootFolderId == null) {
      return this.#loadTree().pipe(switchMap(() => this.#executeSearch(params)))
    }
    return this.#executeSearch(params)
  }

  getFolderTree(): Observable<GalleryFolderTree> {
    return this.#loadTree()
  }

  getItemDetail(id: number): Observable<GalleryItemDetail> {
    return this.#gallery.getOne<RawGalleryItem>(id).pipe(
      map((raw: RawGalleryItem) => {
        const entities = raw.entities ?? []
        const customer = entities.find(entity => entity.type === 'customer') ?? null
        const otherEntity = entities.find(entity => entity.type !== 'customer') ?? null

        return {
          ...this.#mapItem(raw),
          customer: customer ? this.#mapEntity(customer) : null,
          entity: otherEntity ? this.#mapEntity(otherEntity) : null,
          manager:
            raw.manager ?
              {
                id: raw.manager.userCode ?? raw.manager.id ?? '',
                name: raw.manager.name ?? '',
                email: raw.manager.email ?? '',
              }
            : null,
        } satisfies GalleryItemDetail
      })
    )
  }

  getRecentItems(): Observable<GalleryItem[]> {
    return this.#gallery
      .search<RawSearchResponse>({
        columns: SEARCH_COLUMNS,
        filters: {},
        limit: 4,
        offset: 0,
        sortColumn: 'creationDate',
        sortDirection: 'desc',
        additionalParams: {skipPaginationTotalCount: 'true'},
      })
      .pipe(map((res: RawSearchResponse) => (res.data ?? []).map(raw => this.#mapItem(raw))))
  }

  /* Sin uso en f-gallery 2.7.x (ningún componente los invoca) y los filtros van ocultos en cart */
  autocompleteSearch(_category: AutocompleteCategory, _term: string): Observable<AutocompleteResult[]> {
    return of([])
  }

  preloadAutocompleteValue(
    _category: AutocompleteCategory,
    _id: string | number
  ): Observable<AutocompleteResult | null> {
    return of(null)
  }

  #executeSearch(params: GallerySearchParams): Observable<GalleryPaginatedResponse> {
    const filters: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(params.filters ?? {})) {
      if (value === null || value === undefined || value === '') {
        continue
      }
      if (Array.isArray(value)) {
        if (value.length === 0) {
          continue
        }
        filters[key] = value.join(',')
      } else {
        filters[key] = value
      }
    }

    if (params.folderId !== -1) {
      if (params.folderId != null && params.folderId !== 0) {
        filters['node'] = params.folderId
      } else if (this.#rootFolderId != null) {
        filters['node'] = this.#rootFolderId
      }
    }

    if (params.search) {
      filters['q'] = params.search
    }

    const [sortColumn, rawDirection] = (params.sort ?? 'creationDate;desc').split(';')
    const sortDirection: 'asc' | 'desc' = rawDirection === 'asc' ? 'asc' : 'desc'

    return this.#gallery
      .search<RawSearchResponse>({
        columns: SEARCH_COLUMNS,
        filters,
        limit: params.pageSize,
        offset: params.page * params.pageSize,
        sortColumn,
        sortDirection,
      })
      .pipe(
        map((res: RawSearchResponse) => ({
          items: (res.data ?? []).map(raw => this.#mapItem(raw)),
          total: res.pagination?.total ?? res.data?.length ?? 0,
          page: params.page,
          pageSize: params.pageSize,
        }))
      )
  }

  #loadTree(): Observable<GalleryFolderTree> {
    if (!this.#tree$) {
      this.#tree$ = this.#gallery.getTree<RawTreeNode[] | {data?: RawTreeNode[]}>().pipe(
        map((res: RawTreeNode[] | {data?: RawTreeNode[]}) => (Array.isArray(res) ? res : (res?.data ?? []))),
        tap(nodes => {
          if (nodes.length > 0 && nodes[0].id != null) {
            this.#rootFolderId = nodes[0].id
          }
        }),
        map(nodes =>
          nodes.length === 1 ?
            this.#mapFolderNode(nodes[0])
          : this.#mapFolderNode({id: 0, title: 'Gallery', readonly: false, nodes})
        ),
        catchError(error => {
          this.#tree$ = null
          return throwError(() => error)
        }),
        shareReplay(1)
      )
    }
    return this.#tree$
  }

  #mapItem(raw: RawGalleryItem): GalleryItem {
    let visibility: GalleryVisibility | null = null
    if (raw.visibility && typeof raw.visibility === 'object') {
      visibility = {
        id: raw.visibility.id ?? '',
        name: raw.visibility.name ?? raw.visibility.id ?? '',
        roles: raw.visibility.roles ?? [],
      }
    } else if (typeof raw.visibility === 'string') {
      visibility = {id: raw.visibility, name: raw.visibility, roles: []}
    }

    return {
      id: raw.id,
      name: raw.name ?? raw.description ?? `Item ${raw.id}`,
      url: raw.url ?? '',
      urlThumbnail: raw.urlThumbnail ?? null,
      type: raw.type === 'U' || raw.type === 'folder' ? 'folder' : 'file',
      mimeType: raw.mimeType ?? '',
      creationDate: raw.creationDate ?? '',
      readOnly: raw.readOnly ?? false,
      visibility,
      path: raw.path ?? raw.pathCloud ?? '/',
      entities: raw.entities ? raw.entities.map(entity => this.#mapEntity(entity)) : null,
      tags: (raw.tags ?? raw.tagsCloud ?? []).map(tag => ({id: String(tag.id ?? ''), name: tag.name ?? ''})),
      coordX: raw.coordX ?? null,
      coordY: raw.coordY ?? null,
    }
  }

  #mapEntity(raw: RawGalleryEntity): GalleryEntity {
    return {
      id: String(raw.id ?? raw.code ?? ''),
      code: raw.code ?? undefined,
      name: raw.name ?? raw.businessName ?? undefined,
      type: raw.type === 'customer' ? 'customer' : 'entity',
      hierarchyLevel: raw.hierarchyLevel ?? null,
    }
  }

  #mapFolderNode(node: RawTreeNode): GalleryFolderTree {
    return {
      id: node.id ?? 0,
      title: node.title ?? node.name ?? '',
      type: 'folder',
      readonly: node.readonly ?? node.readOnly ?? false,
      nodes: (node.nodes ?? node.children ?? []).map(child => this.#mapFolderNode(child)),
    }
  }
}
